import React, { useState, useEffect, useCallback } from 'react';
import {
  Package, Plus, Search, ChevronDown, Printer, X,
  Clock, CheckCircle, Truck, XCircle, MapPin, Phone, User, Copy,
  ShoppingBag, Trash2, RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import PhoneInput, { isPhoneValide } from '../../components/common/PhoneInput';
import DateRangePicker from '../../components/common/DateRangePicker';
import InputMontant from '../../components/common/InputMontant';
import DocumentImprimable, { SelecteurFormat } from '../../components/common/DocumentImprimable';
import useFormatImpression from '../../components/common/useFormatImpression';
import { getPeriodRange } from '../../components/common/PeriodPills';
import { VILLES_GUINEE_PAR_REGION } from '../../utils/villesGuinee';
import { nettoyerLibelle, getDeviseCourante } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatGNF(n) {
  return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante();
}

// Format court de l'ID commande : 8 premiers caractères en majuscules
// (style "short hash" Git). L'UUID complet reste disponible via le clic
// pour copie dans le presse-papiers et sur le bon de livraison imprimé.
function shortId(id) {
  if (!id) return '';
  return String(id).split('-')[0].toUpperCase();
}

// Copier l'UUID complet dans le presse-papiers + toast confirmation
async function copyId(fullId) {
  if (!fullId) return;
  try {
    await navigator.clipboard.writeText(fullId);
    toast.success('Code complet copié');
  } catch {
    toast.error('Impossible de copier');
  }
}

const STATUTS = {
  en_attente:   { label: 'En attente',    color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', dot: 'bg-orange-400',  icon: Clock },
  confirmee:    { label: 'Confirmée',     color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',       dot: 'bg-blue-400',    icon: CheckCircle },
  en_livraison: { label: 'En livraison',  color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', dot: 'bg-purple-400',  icon: Truck },
  livree:       { label: 'Livrée',        color: 'bg-green-500/20 text-green-400 border-green-500/30',    dot: 'bg-green-400',   icon: CheckCircle },
  annulee:      { label: 'Annulée',       color: 'bg-red-500/20 text-red-400 border-red-500/30',          dot: 'bg-red-400',     icon: XCircle },
};

// Transitions proposées dans le modal de changement de statut.
// Les sauts d'étapes sont autorisés depuis « En attente » :
//   - directement vers « Livrée » (vente comptant immédiate)
//   - directement vers « En livraison » (le livreur est prêt à partir)
// Depuis « Confirmée » aussi, on peut sauter « En livraison » et marquer
// directement « Livrée ».
const TRANSITIONS = {
  en_attente:   [
    { value: 'confirmee',    label: 'Confirmer' },
    { value: 'en_livraison', label: 'En livraison' },
    { value: 'livree',       label: '✓ Marquer livrée' },
    { value: 'annulee',      label: 'Annuler' },
  ],
  confirmee:    [
    { value: 'en_livraison', label: 'Mettre en livraison' },
    { value: 'livree',       label: '✓ Marquer livrée' },
    { value: 'annulee',      label: 'Annuler' },
  ],
  en_livraison: [
    { value: 'livree',       label: '✓ Marquer livrée' },
    { value: 'annulee',      label: 'Annuler' },
  ],
  livree:       [],
  annulee:      [],
};

const MODES_PAIEMENT = [
  { value: '',             label: 'Sélectionner le mode de paiement' },
  { value: 'especes',      label: '💵 Espèces' },
  { value: 'orange_money', label: '🟠 Orange Money' },
  { value: 'mtn_money',    label: '🟡 MTN Money' },
  { value: 'autre',        label: 'Autre' },
];

function StatutBadge({ statut }) {
  const s = STATUTS[statut] || STATUTS.en_attente;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${s.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

// ── Facture imprimable ────────────────────────────────────────────────────────
function FactureImprimable({ commande, onClose }) {
  const [format, setFormat] = useFormatImpression();
  const adresse = [commande.adresse_livraison, commande.quartier && `${commande.quartier}${commande.ville ? ', ' + commande.ville : ''}`]
    .filter(Boolean).join(' — ');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className={`bg-white rounded-xl w-full ${format === 'a4' ? 'max-w-2xl' : 'max-w-md'} shadow-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between gap-2 px-4 pt-4 print:hidden">
          <SelecteurFormat format={format} onChange={setFormat} />
          <button onClick={onClose} title="Fermer"
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="flex gap-2 px-4 pb-3 pt-3 print:hidden">
          <button onClick={() => window.print()}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold text-sm py-2 rounded-lg transition-colors">
            <Printer size={15} /> Imprimer
          </button>
        </div>
        <div className="border-t border-gray-200">
          <DocumentImprimable
            format={format}
            titre="BON DE LIVRAISON"
            reference={`N°${shortId(commande.id)}`}
            date={new Date(commande.date_commande).toLocaleDateString('fr-FR')}
            client={{ nom: commande.client_nom, telephone: commande.client_telephone, adresse }}
            lignes={(commande.lignes || []).map(l => ({
              nom: [l.produit_nom, l.modele, l.taille].filter(Boolean).join(' '),
              qte: Number(l.quantite || 0),
              pu: Number(l.prix_unitaire ?? (l.sous_total / (l.quantite || 1))),
              total: Number(l.sous_total ?? 0),
            }))}
            extras={[
              { label: 'Sous-total', valeur: formatGNF(commande.montant_total) },
              { label: 'Frais livraison', valeur: formatGNF(commande.frais_livraison) },
            ]}
            total={commande.montant_final}
            apresTotal={commande.mode_paiement ? [{ label: 'Paiement', valeur: MODES_PAIEMENT.find(m => m.value === commande.mode_paiement)?.label || commande.mode_paiement }] : []}
            piedMessage="Merci pour votre confiance !"
          />
        </div>
      </div>
    </div>
  );
}

// ── Modal changement de statut ────────────────────────────────────────────────
function ModalStatut({ commande, onClose, onSuccess }) {
  const [nouveau, setNouveau]       = useState('');
  const [noteLiv, setNoteLiv]       = useState('');
  const [modePaie, setModePaie]     = useState(commande.mode_paiement || '');
  const [loading, setLoading]       = useState(false);
  const transitions = TRANSITIONS[commande.statut] || [];

  async function valider() {
    if (!nouveau) return;
    setLoading(true);
    try {
      await api.put(`/commandes/${commande.id}/statut`, {
        nouveau_statut: nouveau,
        note_livraison: noteLiv || undefined,
        mode_paiement: modePaie || undefined,
      });
      toast.success('Statut mis à jour');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">Changer le statut</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-gray-400 text-sm mb-4">
          Commande <strong className="text-white font-mono">#{shortId(commande.id)}</strong> — {commande.client_nom}<br />
          Statut actuel : <StatutBadge statut={commande.statut} />
        </p>
        {transitions.length === 0 ? (
          <p className="text-gray-500 text-sm">Aucune transition possible depuis ce statut.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {transitions.map(t => (
                <button key={t.value}
                  onClick={() => setNouveau(t.value)}
                  className={`py-2.5 px-4 rounded-xl font-medium text-sm transition-all ${
                    nouveau === t.value
                      ? 'bg-yellow-500 text-black font-bold'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
            {nouveau === 'livree' && (
              <>
                <select value={modePaie} onChange={e => setModePaie(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-600 rounded-xl px-3 py-2 text-white">
                  {MODES_PAIEMENT.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <input type="text" value={noteLiv} onChange={e => setNoteLiv(e.target.value)}
                  placeholder="Note de livraison (optionnel)"
                  className="w-full bg-gray-800 border border-gray-600 rounded-xl px-3 py-2 text-white placeholder-gray-500" />
              </>
            )}
            <button onClick={valider} disabled={!nouveau || loading}
              className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-40 text-black font-bold rounded-xl flex items-center justify-center gap-2">
              {loading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : 'Confirmer'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Modal nouvelle commande / édition ─────────────────────────────────────────
// Si la prop `commande` est fournie, le formulaire passe en mode édition :
//   - pré-remplit tous les champs depuis la commande existante
//   - appelle PUT au lieu de POST
//   - change le titre et le bouton d'action
function ModalNouvelleCommande({ onClose, onSuccess, commande }) {
  const estEdition = !!commande;
  const [form, setForm] = useState({
    client_nom:        commande?.client_nom        || '',
    client_telephone:  commande?.client_telephone  || '',
    adresse_livraison: commande?.adresse_livraison || '',
    quartier:          commande?.quartier          || '',
    ville:             commande?.ville             || 'Conakry',
    frais_livraison:   commande?.frais_livraison ?? 0,
    mode_paiement:     commande?.mode_paiement     || '',
    notes:             commande?.notes             || '',
  });
  const [variantes, setVariantes] = useState([]);
  const [cartons, setCartons]     = useState([]);
  const [search, setSearch]       = useState('');
  const [resultats, setResultats] = useState([]);
  // Clé unifiée d'une ligne : conditionnement (carton) OU article (variante).
  const [lignes, setLignes]       = useState(
    // Reconstruire les lignes du panier depuis la commande en édition
    // (les lignes carton sont conservées telles quelles, cf. migration 052).
    commande?.lignes
      ? commande.lignes.map(l => ({
          cle:           l.carton_id ? `c-${l.carton_id}` : `v-${l.variante_id}`,
          variante_id:   l.variante_id || null,
          carton_id:     l.carton_id || null,
          produit_nom:   l.produit_nom,
          modele:        l.modele,
          taille:        l.taille,
          quantite:      parseInt(l.quantite) || 1,
          prix_unitaire: parseFloat(l.prix_unitaire) || 0,
        }))
      : []
  );
  const searchRef = React.useRef(null);
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    api.get('/stock?limit=500').then(({ data }) => setVariantes(data.stock || [])).catch(() => {});
    api.get('/cartons').then(({ data }) => setCartons(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!search.trim()) { setResultats([]); return; }
    const q = search.toLowerCase();
    // Conditionnements (lots) d'abord
    const lots = cartons.filter(c =>
      c.nom?.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q)
    ).map(c => ({
      _type: 'carton', carton_id: c.id,
      produit_nom: `${c.nom}`,
      modele: '',   // on garde uniquement le nom du conditionnement (lisible)
      taille: '',
      prix_vente_livraison: Number(c.prix_vente_livraison) || Number(c.prix_vente_magasin) || 0,
    }));
    const arts = variantes.filter(v =>
      v.produit_nom?.toLowerCase().includes(q) || v.modele?.toLowerCase().includes(q)
    ).map(v => ({ ...v, _type: 'variante' }));
    setResultats([...lots, ...arts].slice(0, 10));
  }, [search, variantes, cartons]);

  function ajouterLigne(v) {
    // Le dernier article ajouté (ou ré-ajouté) remonte en tête : c'est plus
    // intuitif pour un opérateur qui construit sa commande au fil de l'eau.
    const cle = v._type === 'carton' ? `c-${v.carton_id}` : `v-${v.variante_id}`;
    // Nombre de lignes APRÈS ajout, calculé depuis l'état courant (closure).
    // Ne PAS le déduire dans l'updater setLignes : celui-ci s'exécute de façon
    // différée, le toast lirait alors une valeur encore à 0.
    const existeDeja = lignes.some(l => l.cle === cle);
    const nbApres = existeDeja ? lignes.length : lignes.length + 1;
    setLignes(prev => {
      const ex = prev.find(l => l.cle === cle);
      if (ex) {
        const autres = prev.filter(l => l.cle !== cle);
        return [{ ...ex, quantite: ex.quantite + 1 }, ...autres];
      }
      return [{
        cle,
        variante_id: v._type === 'carton' ? null : v.variante_id,
        carton_id:   v._type === 'carton' ? v.carton_id : null,
        produit_nom: v.produit_nom,
        modele: v.modele,
        taille: v.taille,
        quantite: 1,
        // NUMERIC PostgreSQL → chaîne côté Node : "0" est truthy, donc on
        // convertit en nombre avant le || pour basculer correctement sur le
        // prix magasin quand le prix livraison vaut 0.
        prix_unitaire: Number(v.prix_vente_livraison) || Number(v.prix_vente_magasin) || 0,
      }, ...prev];
    });
    // Toast de confirmation visible : le caissier sait immédiatement que le
    // clic a été enregistré et combien d'articles il a déjà dans son panier.
    toast.success(
      `✓ ${v.produit_nom} ${v.modele || ''} ajouté — ${nbApres} article${nbApres > 1 ? 's' : ''} dans la commande`,
      { duration: 2000 }
    );
    setSearch('');
    setTimeout(() => searchRef.current?.focus(), 0);
  }

  const total = lignes.reduce((s, l) => s + l.quantite * l.prix_unitaire, 0);
  const totalFinal = total + parseFloat(form.frais_livraison || 0);

  async function soumettre(confirmerPerte = false) {
    if (!form.client_nom) return toast.error('Nom du client requis');
    if (!isPhoneValide(form.client_telephone)) return toast.error('Numéro invalide — format attendu : +224 6xx xx xx xx');
    if (lignes.length === 0) return toast.error('Au moins un article requis');
    setLoading(true);
    try {
      if (estEdition) {
        await api.put(`/commandes/${commande.id}`, { ...form, lignes });
        toast.success('Commande modifiée');
      } else {
        await api.post('/commandes', { ...form, lignes, confirmer_vente_perte: confirmerPerte });
        toast.success('Commande créée !');
      }
      onSuccess();
    } catch (err) {
      // Garde-fou rentabilité : article(s) sous le coût → confirmation.
      if (err.response?.status === 409 && err.response.data?.code === 'VENTE_A_PERTE' && !confirmerPerte) {
        const recap = (err.response.data.lignes || [])
          .map(l => `• ${l.libelle || 'article'} : ${formatGNF(l.prix)} < coût ${formatGNF(l.cout)}`)
          .join('\n');
        if (window.confirm(`⚠️ Vente à perte détectée\n\n${recap}\n\nValider la commande à ce prix ?`)) {
          return soumettre(true);
        }
        return;
      }
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }

  const inp = 'w-full bg-gray-800 border border-gray-600 rounded-xl px-3 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 text-sm';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-lg font-bold text-white">
            {estEdition ? `Modifier la commande #${String(commande.id).split('-')[0].toUpperCase()}` : 'Nouvelle commande'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Infos client */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="block text-xs text-gray-400">Nom du client <span className="text-yellow-500">*</span></label>
              <input className={inp} placeholder="Ex : Mamadou Diallo" value={form.client_nom} onChange={e => setForm({...form, client_nom: e.target.value})} />
            </div>
            <PhoneInput label="Téléphone" value={form.client_telephone} onChange={e => setForm({...form, client_telephone: e.target.value})} required />
            <div className="space-y-1">
              <label className="block text-xs text-gray-400">Adresse de livraison</label>
              <input className={inp} placeholder="Rue, immeuble…" value={form.adresse_livraison} onChange={e => setForm({...form, adresse_livraison: e.target.value})} />
            </div>
            <div className="space-y-1">
              <label className="block text-xs text-gray-400">Quartier</label>
              <input className={inp} placeholder="Ex : Madina, Kipé…" value={form.quartier} onChange={e => setForm({...form, quartier: e.target.value})} />
            </div>
            <div className="space-y-1">
              <label className="block text-xs text-gray-400">Ville</label>
              <select
                className={inp}
                value={form.ville || 'Conakry'}
                onChange={e => setForm({...form, ville: e.target.value})}
              >
                {Object.entries(VILLES_GUINEE_PAR_REGION).map(([region, villes]) => (
                  <optgroup key={region} label={region}>
                    {villes.map(v => <option key={v} value={v}>{v}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="block text-xs text-gray-400">Frais de livraison ({getDeviseCourante()})</label>
              <InputMontant
                className={inp}
                placeholder="Ex : 15 000"
                value={form.frais_livraison}
                onChange={v => setForm({...form, frais_livraison: v})}
              />
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              PANIER DE LA COMMANDE — toujours visible
              On encadre tout dans un bloc avec bordure et titre clair pour
              que l'utilisateur comprenne d'un coup d'œil que c'est un panier
              dans lequel on peut ajouter PLUSIEURS articles.
              ═══════════════════════════════════════════════════════════════ */}
          <div className="border border-gray-700 rounded-2xl p-4 bg-gray-950/40 space-y-3">
            {/* En-tête du panier */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <ShoppingBag size={16} className="text-yellow-400" />
                Articles à commander
              </h3>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                lignes.length === 0
                  ? 'bg-gray-800 text-gray-500'
                  : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
              }`}>
                {lignes.length} article{lignes.length > 1 ? 's' : ''}
              </span>
            </div>

            {/* Champ recherche — toujours visible, place explicite pour ajouter */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-yellow-400" size={16} />
              <input
                ref={searchRef}
                className={`${inp} pl-9 border-yellow-600/40`}
                placeholder={lignes.length === 0
                  ? '🔍 Cherche un article à ajouter (ex : valise, sac…)'
                  : `🔍 Ajouter encore un article (déjà ${lignes.length} dans la commande)`}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {resultats.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-gray-800 border border-yellow-600/50 rounded-xl overflow-hidden shadow-2xl">
                  {resultats.map(v => (
                    <button
                      type="button"
                      key={v._type === 'carton' ? `c-${v.carton_id}` : `v-${v.variante_id}`}
                      onClick={() => ajouterLigne(v)}
                      className={`w-full flex justify-between items-center p-3 hover:bg-yellow-500/10 text-left text-sm border-b border-gray-700 last:border-b-0 ${v._type === 'carton' ? 'border-l-2 border-l-yellow-500/60' : ''}`}
                    >
                      <span className="text-white">
                        <Plus size={12} className="inline mr-1.5 text-yellow-400" />
                        {v.produit_nom} {nettoyerLibelle(v.modele)} {v.taille ? `— ${v.taille}` : ''}
                        {v._type === 'carton' && (
                          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300">Lot</span>
                        )}
                      </span>
                      <span className="text-yellow-400 font-bold">{formatGNF(Number(v.prix_vente_livraison) || Number(v.prix_vente_magasin))}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Liste des lignes — état vide explicite */}
            {lignes.length === 0 ? (
              <div className="border-2 border-dashed border-gray-700 rounded-xl p-6 text-center">
                <ShoppingBag size={28} className="mx-auto text-gray-600 mb-2" />
                <p className="text-sm text-gray-400 font-medium">Panier vide</p>
                <p className="text-xs text-gray-500 mt-1">
                  Utilise la recherche ci-dessus pour ajouter un ou plusieurs articles à cette commande.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {lignes.map((l) => (
                  <div key={l.cle} className="flex items-center gap-2 bg-gray-800 rounded-xl p-2 border border-gray-700">
                    <span className="flex-1 text-sm text-white truncate">{l.produit_nom} {l.modele} {l.taille}</span>
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-gray-500">Qté</span>
                      <input
                        type="number"
                        value={l.quantite || ''}
                        min={1}
                        placeholder="0"
                        onChange={e => setLignes(prev => prev.map(x => x.cle === l.cle
                          ? { ...x, quantite: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }
                          : x))}
                        className="w-14 bg-gray-700 text-white text-center rounded-lg px-2 py-1 text-sm"
                      />
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-gray-500">Prix</span>
                      <InputMontant
                        value={l.prix_unitaire}
                        placeholder={`0 ${getDeviseCourante()}`}
                        onChange={v => setLignes(prev => prev.map(x => x.cle === l.cle
                          ? { ...x, prix_unitaire: v }
                          : x))}
                        className="w-28 bg-gray-700 text-yellow-400 text-right rounded-lg px-2 py-1 text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setLignes(prev => prev.filter(x => x.cle !== l.cle))}
                      className="text-gray-500 hover:text-red-400 p-1"
                      title="Retirer cet article du panier"
                    >
                      <Trash2 size={14}/>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Sous-total / total — affiché dès qu'il y a au moins un article */}
            {lignes.length > 0 && (
              <div className="border-t border-gray-700 pt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Sous-total articles</span>
                  <span className="text-white">{formatGNF(total)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Frais de livraison</span>
                  <span className="text-gray-300">{formatGNF(parseFloat(form.frais_livraison) || 0)}</span>
                </div>
                <div className="flex justify-between text-base font-bold pt-1">
                  <span className="text-white">Total</span>
                  <span className="text-yellow-400">{formatGNF(totalFinal)}</span>
                </div>
              </div>
            )}
          </div>

          <textarea className={inp} rows={2} placeholder="Notes (optionnel)" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
        </div>
        <div className="p-6 border-t border-gray-700">
          <button onClick={() => soumettre()} disabled={loading}
            className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-40 text-black font-bold rounded-xl flex items-center justify-center gap-2">
            {loading
              ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              : estEdition
                ? <><CheckCircle size={18} /> Enregistrer les modifications</>
                : <><Plus size={18} /> Créer la commande</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────
export default function Commandes() {
  const { canEditBusiness } = useAuth();
  const [commandes, setCommandes]   = useState([]);
  const [parStatut, setParStatut]   = useState({});
  const [filtre, setFiltre]         = useState('tous');
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(true);
  const [modalStatut, setModalStatut]   = useState(null);
  const [modalNvCmd, setModalNvCmd]     = useState(false);
  const [editCmd, setEditCmd]           = useState(null); // commande à modifier
  const [facture, setFacture]           = useState(null);
  const [commandeOpen, setCommandeOpen] = useState(null);
  // Filtre par période (vide = toutes les commandes, tous statuts confondus).
  // Les presets (Aujourd'hui/Hier/7j/30j/...) sont fournis par DateRangePicker.
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin]     = useState('');

  const refreshAll = () => {
    const t = getPeriodRange('today');
    setDateDebut(t.from); setDateFin(t.to);
    setFiltre('tous'); setSearch('');
  };

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filtre !== 'tous') params.append('statut', filtre);
      if (search) params.append('search', search);
      if (dateDebut) params.append('date_debut', dateDebut);
      if (dateFin)   params.append('date_fin',   dateFin);
      const { data } = await api.get(`/commandes?${params}`);
      setCommandes(data.commandes || []);
      setParStatut(data.parStatut || {});
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch { toast.error('Erreur chargement commandes'); }
    finally { setLoading(false); }
  }, [filtre, search, page, dateDebut, dateFin]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { setPage(1); }, [filtre, search, dateDebut, dateFin]);

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white">Commandes en ligne</h1>
          <p className="text-gray-400 text-sm">{total} commande{total > 1 ? 's' : ''} au total</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshAll}
            className="p-2.5 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-xl transition-all"
            title="Actualiser (remet à Aujourd'hui)"
          >
            <RefreshCw size={16} />
          </button>
          {canEditBusiness && (
            <button onClick={() => setModalNvCmd(true)}
              className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-4 py-2.5 rounded-xl transition-all">
              <Plus size={18} /> Nouvelle commande
            </button>
          )}
        </div>
      </div>

      {/* Filtres statut */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFiltre('tous')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filtre === 'tous' ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
          Toutes ({total})
        </button>
        {Object.entries(STATUTS).map(([key, s]) => (
          <button key={key} onClick={() => setFiltre(key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filtre === key ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
            {s.label} {parStatut[key] ? `(${parStatut[key]})` : ''}
          </button>
        ))}
      </div>

      {/* Filtres date + recherche */}
      <div className="bg-gray-800/40 border border-gray-700 rounded-2xl p-4 space-y-3">
        <DateRangePicker
          debut={dateDebut}
          fin={dateFin}
          onChange={(d, f) => { setDateDebut(d); setDateFin(f); }}
        />
        {(dateDebut || dateFin) && (
          <button
            type="button"
            onClick={() => { setDateDebut(''); setDateFin(''); }}
            className="text-xs text-gray-400 hover:text-yellow-400 transition-colors"
          >
            ✕ Effacer le filtre de période
          </button>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom ou téléphone…"
            className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-9 pr-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 text-sm" />
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : commandes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-600">
          <Package size={40} className="mb-3 opacity-40" />
          <p>Aucune commande</p>
        </div>
      ) : (
        <div className="space-y-3 flex-1 overflow-y-auto">
          {commandes.map(c => {
            const isOpen = commandeOpen === c.id;
            return (
              <div key={c.id} className={`bg-gray-800/60 border rounded-2xl overflow-hidden transition-all ${
                isOpen ? 'border-yellow-500/50' : 'border-gray-700'
              }`}>
                {/* Ligne principale */}
                <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => setCommandeOpen(isOpen ? null : c.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); copyId(c.id); }}
                        title={`Copier le code complet — ${c.id}`}
                        className="group inline-flex items-center gap-1 text-white font-bold font-mono text-sm bg-gray-900/60 hover:bg-gray-700/70 border border-gray-700 hover:border-yellow-500/60 rounded-md px-2 py-0.5 transition-colors"
                      >
                        <span>#{shortId(c.id)}</span>
                        <Copy size={11} className="text-gray-500 group-hover:text-yellow-400 transition-colors" />
                      </button>
                      <StatutBadge statut={c.statut} />
                      <span className="text-gray-400 text-xs">{new Date(c.date_commande).toLocaleDateString('fr-FR')}</span>
                      {/* Date de livraison effective — affichée seulement si différente
                          de la date de création, pour ne pas surcharger l'UI. */}
                      {c.statut === 'livree' && c.date_livraison
                        && c.date_livraison !== c.date_commande && (
                        <span className="text-green-400/80 text-xs inline-flex items-center gap-1">
                          <CheckCircle size={11} />
                          livré le {new Date(c.date_livraison).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm">
                      <span className="text-gray-200 flex items-center gap-1"><User size={12} />{c.client_nom}</span>
                      <span className="text-gray-400 flex items-center gap-1"><Phone size={12} />{c.client_telephone}</span>
                      {c.quartier && <span className="text-gray-500 flex items-center gap-1"><MapPin size={12}/>{c.quartier}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-yellow-400 font-bold">{formatGNF(c.montant_final)}</p>
                    <p className="text-gray-500 text-xs">{c.nb_articles} art.</p>
                  </div>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>

                {/* Détail */}
                {isOpen && (
                  <div className="border-t border-gray-700 p-4 space-y-4">
                    {/* Articles */}
                    {c.lignes && (
                      <div>
                        <p className="text-xs text-gray-400 mb-2 font-medium">Articles</p>
                        <div className="space-y-1">
                          {c.lignes.map((l, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span className="text-gray-300">{l.produit_nom} {l.modele} {l.taille} ×{l.quantite}</span>
                              <span className="text-white">{formatGNF(l.sous_total)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-700">
                          <span className="text-gray-400">Frais livraison</span>
                          <span className="text-gray-300">{formatGNF(c.frais_livraison)}</span>
                        </div>
                        <div className="flex justify-between font-bold mt-1">
                          <span className="text-gray-200">Total</span>
                          <span className="text-yellow-400">{formatGNF(c.montant_final)}</span>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {(c.notes || c.note_livraison) && (
                      <div className="bg-gray-900/50 rounded-xl p-3 text-sm text-gray-400">
                        {c.notes && <p>📝 {c.notes}</p>}
                        {c.note_livraison && <p className="text-blue-400 mt-1">🏍️ {c.note_livraison}</p>}
                      </div>
                    )}

                    {/* Actions — cachées pour l'admin (lecture seule) */}
                    <div className="flex gap-2 flex-wrap">
                      {canEditBusiness && TRANSITIONS[c.statut]?.length > 0 && (
                        <button onClick={() => setModalStatut(c)}
                          className="flex-1 py-2 bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded-xl text-sm font-medium transition-all">
                          Changer statut
                        </button>
                      )}
                      {/* Modification possible uniquement tant que la commande
                          est en_attente (avant toute réservation de stock). */}
                      {canEditBusiness && c.statut === 'en_attente' && (
                        <button onClick={() => setEditCmd(c)}
                          className="flex items-center gap-1.5 px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm transition-all"
                          title="Modifier client, articles, livraison…">
                          ✏️ Modifier
                        </button>
                      )}
                      <button onClick={() => setFacture(c)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-xl text-sm transition-all">
                        <Printer size={14} /> Facture
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 pt-2">
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-xl disabled:opacity-40 text-sm hover:bg-gray-700">← Préc.</button>
          <span className="px-4 py-2 text-gray-400 text-sm">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-xl disabled:opacity-40 text-sm hover:bg-gray-700">Suiv. →</button>
        </div>
      )}

      {/* Modals */}
      {modalStatut && <ModalStatut commande={modalStatut} onClose={() => setModalStatut(null)} onSuccess={() => { setModalStatut(null); charger(); }} />}
      {modalNvCmd && <ModalNouvelleCommande onClose={() => setModalNvCmd(false)} onSuccess={() => { setModalNvCmd(false); charger(); }} />}
      {editCmd && (
        <ModalNouvelleCommande
          commande={editCmd}
          onClose={() => setEditCmd(null)}
          onSuccess={() => { setEditCmd(null); charger(); }}
        />
      )}
      {facture && <FactureImprimable commande={facture} onClose={() => setFacture(null)} />}
    </div>
  );
}
